import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from './SEOHead';
import toast from 'react-hot-toast';

interface LoginPageProps {
  onLoginSuccess?: () => void;
  onShowLogin?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess, onShowLogin }) => {
  const { login, loginWithToken, register } = useAuth();
  const [showRegister, setShowRegister] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // 登录方式: 'password' | 'code'
  const [loginMode, setLoginMode] = useState<'password' | 'code'>('password');
  
  // 验证码登录状态
  const [codeEmail, setCodeEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  // 注册表单状态
  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  
  // 密码显示状态
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 倒计时效果
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 发送验证码
  const handleSendCode = async () => {
    if (!codeEmail) {
      toast.error('请输入邮箱地址');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(codeEmail)) {
      toast.error('请输入有效的邮箱地址');
      return;
    }

    setIsSendingCode(true);
    try {
      const response = await fetch('/api/auth/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: codeEmail })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('验证码已发送，请查收邮箱');
        setCountdown(60);
      } else {
        toast.error(data.message || '发送失败');
      }
    } catch (error) {
      toast.error('发送失败，请稍后再试');
    } finally {
      setIsSendingCode(false);
    }
  };

  // 验证码登录
  const handleCodeLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!codeEmail || !verificationCode) {
      toast.error('请输入邮箱和验证码');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: codeEmail, code: verificationCode })
      });
      
      const data = await response.json();
      
      if (data.success) {
        loginWithToken(data.token);
        toast.success('登录成功');
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        toast.error(data.message || '登录失败');
      }
    } catch (error) {
      toast.error('登录失败，请稍后再试');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    
    const formData = new FormData(e.currentTarget);
    const credentials = {
      username: formData.get('username') as string,
      password: formData.get('password') as string,
    };

    try {
      await login(credentials);
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    } catch (error) {
      // 错误已在context中处理
    } finally {
      setIsLoading(false);
    }
  };

  // 验证密码
  const validatePassword = (password: string, confirmPassword: string) => {
    const errors: string[] = [];
    
    if (password.length < 6) {
      errors.push('密码长度至少6位');
    }
    
    if (confirmPassword && password !== confirmPassword) {
      errors.push('两次输入的密码不一致');
    }
    
    setPasswordErrors(errors);
    return errors.length === 0;
  };

  // 处理注册表单输入变化
  const handleRegisterInputChange = (field: keyof typeof registerForm, value: string) => {
    const newForm = { ...registerForm, [field]: value };
    setRegisterForm(newForm);
    
    // 实时验证密码
    if (field === 'password' || field === 'confirmPassword') {
      validatePassword(newForm.password, newForm.confirmPassword);
    }
  };

  const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    
    // 最终验证
    if (!validatePassword(registerForm.password, registerForm.confirmPassword)) {
      toast.error('请检查密码格式和确认密码');
      setIsLoading(false);
      return;
    }

    if (!registerForm.username.trim() || !registerForm.email.trim()) {
      toast.error('请填写完整信息');
      setIsLoading(false);
      return;
    }

    const credentials = {
      username: registerForm.username.trim(),
      email: registerForm.email.trim(),
      password: registerForm.password,
    };

    try {
      await register(credentials);
      setShowRegister(false);
      // 重置表单
      setRegisterForm({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
      });
      setPasswordErrors([]);
      toast.success('注册成功！请使用新账户登录');
      // 注册成功后不调用onLoginSuccess，让用户手动登录
      if (onShowLogin) {
        onShowLogin();
      }
    } catch (error) {
      // 错误已在context中处理
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <SEOHead 
        description="登录AI提示词管理系统，开始管理您的AI提示词，提高创作效率"
        keywords="AI提示词登录,Prompt管理,用户登录,AI工具"
      />
      
      {/* 动态几何背景元素 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* 大圆形 - 右上角 */}
        <motion.div
          className="absolute -top-20 -right-20 w-96 h-96 bg-gradient-to-br from-primary/20 to-purple-500/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* 中等圆形 - 左下角 */}
        <motion.div
          className="absolute -bottom-32 -left-32 w-80 h-80 bg-gradient-to-tr from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.2, 0.4],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* 浮动三角形 */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-0 h-0 border-l-[30px] border-l-transparent border-r-[30px] border-r-transparent border-b-[52px] border-b-primary/30 dark:border-b-primary/20"
          animate={{
            y: [0, -30, 0],
            rotate: [0, 180, 360],
            opacity: [0.6, 0.8, 0.6],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* 浮动正方形 */}
        <motion.div
          className="absolute top-1/3 right-1/4 w-16 h-16 border-2 border-purple-500/30 dark:border-purple-400/20 rounded-lg"
          animate={{
            y: [0, 20, 0],
            rotate: [0, 90, 0],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />
        
        {/* 浮动圆环 */}
        <motion.div
          className="absolute bottom-1/4 right-1/3 w-20 h-20 border-2 border-cyan-500/30 dark:border-cyan-400/20 rounded-full"
          animate={{
            y: [0, -25, 0],
            x: [0, 15, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
        />
        
        {/* 小圆点群 */}
        <motion.div
          className="absolute top-1/2 left-1/6 w-4 h-4 bg-primary/40 rounded-full"
          animate={{
            y: [0, -40, 0],
            opacity: [0.4, 0.8, 0.4],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute top-2/3 left-1/5 w-3 h-3 bg-purple-500/40 rounded-full"
          animate={{
            y: [0, -30, 0],
            opacity: [0.3, 0.7, 0.3],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5,
          }}
        />
        <motion.div
          className="absolute top-1/4 right-1/6 w-5 h-5 bg-cyan-500/40 rounded-full"
          animate={{
            y: [0, -35, 0],
            opacity: [0.5, 0.9, 0.5],
          }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5,
          }}
        />
        
        {/* 六边形 */}
        <motion.div
          className="absolute bottom-1/3 left-1/3"
          animate={{
            y: [0, 20, 0],
            rotate: [0, 60, 0],
            opacity: [0.4, 0.7, 0.4],
          }}
          transition={{
            duration: 11,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3,
          }}
        >
          <svg width="50" height="50" viewBox="0 0 50 50" className="fill-none stroke-primary/30 dark:stroke-primary/20 stroke-2">
            <polygon points="25,2 47,15 47,35 25,48 3,35 3,15" />
          </svg>
        </motion.div>
        
        {/* 菱形 */}
        <motion.div
          className="absolute top-1/5 left-1/2 w-12 h-12 border-2 border-orange-500/30 dark:border-orange-400/20 rotate-45"
          animate={{
            y: [0, -20, 0],
            scale: [1, 1.2, 1],
            opacity: [0.4, 0.7, 0.4],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 4,
          }}
        />
        
        {/* 渐变网格线条 */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.02]">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(to right, currentColor 1px, transparent 1px),
              linear-gradient(to bottom, currentColor 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }} />
        </div>
      </div>
      
      <div className="container mx-auto px-6 z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-6xl lg:text-8xl font-black mb-6">
              <span className="gradient-text">AI</span><br />
              <span className="text-gray-900 dark:text-white">Prompt</span><br />
              <span className="text-gray-600 dark:text-gray-300 text-4xl lg:text-6xl font-light">Manager</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              智能化管理您的AI提示词，<br />提升创作效率与质量
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-2xl border border-gray-100 dark:border-gray-700 transition-colors duration-200"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-user text-primary text-2xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">欢迎登录</h2>
              <p className="text-gray-600 dark:text-gray-300">请选择登录方式</p>
            </div>

            {/* 登录方式切换 */}
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1 mb-6">
              <button
                type="button"
                onClick={() => setLoginMode('password')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  loginMode === 'password'
                    ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-primary'
                }`}
              >
                <i className="fas fa-lock mr-2"></i>密码登录
              </button>
              <button
                type="button"
                onClick={() => setLoginMode('code')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  loginMode === 'code'
                    ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-primary'
                }`}
              >
                <i className="fas fa-envelope mr-2"></i>验证码登录
              </button>
            </div>

            {/* 密码登录表单 */}
            {loginMode === 'password' && (
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">用户名或邮箱</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="username"
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 pl-12 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="请输入用户名或邮箱"
                    />
                    <i className="fas fa-user absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"></i>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">密码</label>
                  <div className="relative">
                    <input
                      type={showLoginPassword ? "text" : "password"}
                      name="password"
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 pl-12 pr-12 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="请输入密码"
                    />
                    <i className="fas fa-lock absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"></i>
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      title={showLoginPassword ? "隐藏密码" : "显示密码"}
                    >
                      <i className={`fas ${showLoginPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 rounded-xl transition-all duration-200 transform hover:scale-105 primary-glow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <><i className="fas fa-spinner fa-spin mr-2"></i>登录中...</>
                  ) : (
                    <><i className="fas fa-sign-in-alt mr-2"></i>登录系统</>
                  )}
                </button>
              </form>
            )}

            {/* 验证码登录表单 */}
            {loginMode === 'code' && (
              <form onSubmit={handleCodeLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">注册邮箱</label>
                  <div className="relative">
                    <input
                      type="email"
                      value={codeEmail}
                      onChange={(e) => setCodeEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 pl-12 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="请输入注册时使用的邮箱"
                    />
                    <i className="fas fa-envelope absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"></i>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">验证码</label>
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        required
                        maxLength={6}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 pl-12 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 tracking-widest text-center font-mono text-lg"
                        placeholder="6位验证码"
                      />
                      <i className="fas fa-shield-alt absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"></i>
                    </div>
                    <button
                      type="button"
                      onClick={handleSendCode}
                      disabled={isSendingCode || countdown > 0}
                      className="px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap min-w-[100px]"
                    >
                      {isSendingCode ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : countdown > 0 ? (
                        `${countdown}s`
                      ) : (
                        '获取验证码'
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || verificationCode.length !== 6}
                  className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 rounded-xl transition-all duration-200 transform hover:scale-105 primary-glow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <><i className="fas fa-spinner fa-spin mr-2"></i>登录中...</>
                  ) : (
                    <><i className="fas fa-sign-in-alt mr-2"></i>登录系统</>
                  )}
                </button>
              </form>
            )}

            <div className="mt-6 text-center">
              <button
                onClick={() => setShowRegister(true)}
                className="text-primary hover:text-primary-dark font-medium transition-colors duration-200"
              >
                还没有账号？立即注册
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* 注册模态框 */}
      <AnimatePresence>
        {showRegister && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto transition-colors duration-200"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">创建账户</h3>
                <button
                  onClick={() => {
                    setShowRegister(false);
                    setRegisterForm({
                      username: '',
                      email: '',
                      password: '',
                      confirmPassword: ''
                    });
                    setPasswordErrors([]);
                    setShowRegisterPassword(false);
                    setShowConfirmPassword(false);
                  }}
                  title="关闭注册对话框"
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">用户名</label>
                  <input
                    type="text"
                    value={registerForm.username}
                    onChange={(e) => handleRegisterInputChange('username', e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="选择一个用户名"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">邮箱</label>
                  <input
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => handleRegisterInputChange('email', e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="输入您的邮箱"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">密码</label>
                  <div className="relative">
                    <input
                      type={showRegisterPassword ? "text" : "password"}
                      value={registerForm.password}
                      onChange={(e) => handleRegisterInputChange('password', e.target.value)}
                      required
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent pr-20 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                        passwordErrors.length > 0 && registerForm.password 
                          ? 'border-red-300 dark:border-red-500 bg-red-50 dark:bg-red-900/20' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="设置密码（至少6位）"
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                      {registerForm.password && (
                        <div>
                          {passwordErrors.length === 0 ? (
                            <i className="fas fa-check text-green-500"></i>
                          ) : (
                            <i className="fas fa-exclamation-triangle text-red-500"></i>
                          )}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                        className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        title={showRegisterPassword ? "隐藏密码" : "显示密码"}
                      >
                        <i className={`fas ${showRegisterPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>
                  
                  {/* 密码要求提示 */}
                  <div className="mt-2 space-y-1">
                    <div className={`text-xs flex items-center ${
                      registerForm.password.length >= 6 ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      <i className={`fas ${
                        registerForm.password.length >= 6 ? 'fa-check' : 'fa-circle'
                      } mr-2 text-xs`}></i>
                      至少6位字符
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">确认密码</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={registerForm.confirmPassword}
                      onChange={(e) => handleRegisterInputChange('confirmPassword', e.target.value)}
                      required
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent pr-20 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                        registerForm.confirmPassword && registerForm.password !== registerForm.confirmPassword
                          ? 'border-red-300 dark:border-red-500 bg-red-50 dark:bg-red-900/20' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="再次输入密码"
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                      {registerForm.confirmPassword && (
                        <div>
                          {registerForm.password === registerForm.confirmPassword ? (
                            <i className="fas fa-check text-green-500"></i>
                          ) : (
                            <i className="fas fa-times text-red-500"></i>
                          )}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        title={showConfirmPassword ? "隐藏密码" : "显示密码"}
                      >
                        <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>
                  
                  {/* 密码确认提示 */}
                  {registerForm.confirmPassword && (
                    <div className={`mt-2 text-xs flex items-center ${
                      registerForm.password === registerForm.confirmPassword ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      <i className={`fas ${
                        registerForm.password === registerForm.confirmPassword ? 'fa-check' : 'fa-times'
                      } mr-2 text-xs`}></i>
                      {registerForm.password === registerForm.confirmPassword ? '密码匹配' : '密码不一致'}
                    </div>
                  )}
                </div>

                {/* 错误提示 */}
                {passwordErrors.length > 0 && registerForm.password && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
                    <div className="flex items-start">
                      <i className="fas fa-exclamation-triangle text-red-500 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0"></i>
                      <div className="text-sm text-red-700 dark:text-red-300">
                        <div className="font-medium mb-1">密码格式不正确：</div>
                        <ul className="space-y-1">
                          {passwordErrors.map((error, index) => (
                            <li key={index} className="flex items-center">
                              <span className="w-1 h-1 bg-red-500 dark:bg-red-400 rounded-full mr-2 flex-shrink-0"></span>
                              {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRegister(false);
                      setRegisterForm({
                        username: '',
                        email: '',
                        password: '',
                        confirmPassword: ''
                      });
                      setPasswordErrors([]);
                      setShowRegisterPassword(false);
                      setShowConfirmPassword(false);
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || passwordErrors.length > 0 || !registerForm.password || !registerForm.confirmPassword}
                    className="flex-1 bg-primary hover:bg-primary-dark text-white py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isLoading ? (
                      <><i className="fas fa-spinner fa-spin mr-2"></i>注册中...</>
                    ) : (
                      '注册账户'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default LoginPage; 